// src/utils/premiumAccess.js (web)
//
// Miroir CLIENT de functions/utils/premiumAccess.js. Les deux doivent rester alignés :
// si l'UI et le serveur divergent, l'utilisateur voit un écran de blocage sur une
// fonctionnalité que le backend lui accorde (ou l'inverse).
//
// Deux voies d'accès, une seule question :
//   1. abonnement payant → subscription_type === 'premium'
//   2. essai gratuit     → trial_ends_at encore dans le futur
//
// ⚠️ Ce module ne décide RIEN de sensible : la frontière réelle est côté serveur
// (analyzeBloodTest, saveHealthProfileV2, handleChat). Ici on ne fait qu'éviter
// d'afficher un mur de paywall à quelqu'un que le backend laisse passer.

/**
 * Normalise une date d'expiration d'essai en millisecondes epoch.
 *
 * Côté client, trial_ends_at arrive normalement comme Timestamp du SDK Firestore
 * (toMillis/toDate), mais peut aussi être absent, null, une Date, un nombre, une
 * chaîne ISO, ou un objet brut {seconds, nanoseconds} après sérialisation.
 * Toute valeur non interprétable retourne 0 → aucun accès accordé (fail-closed).
 *
 * @param {*} value - valeur du champ trial_ends_at, potentiellement null/absente
 * @returns {number} millisecondes epoch, ou 0 si absent/illisible
 */
function toEpochMillis(value) {
  if (value === null || value === undefined) return 0;

  try {
    // Timestamp firebase/firestore (SDK client) et firebase-admin
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();

    if (value instanceof Date) return value.getTime();

    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    // Timestamp sérialisé : {seconds, nanoseconds} côté client,
    // {_seconds, _nanoseconds} après un aller-retour JSON côté admin.
    const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
    if (typeof seconds === 'number') {
      const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
      return seconds * 1000 + nanos / 1e6;
    }
  } catch {
    // Un Timestamp corrompu ne doit jamais faire planter un écran.
    return 0;
  }

  return 0;
}

/**
 * L'utilisateur a-t-il accès aux fonctionnalités Premium (abonnement OU essai actif) ?
 *
 * @param {object|null|undefined} userData - le document users/{uid} (ou l'objet `user`
 *   fusionné du AuthContext, qui porte subscription_type et trial_ends_at)
 * @returns {boolean}
 */
export function hasPremiumAccess(userData) {
  if (!userData) return false;
  if (userData.subscription_type === 'premium') return true;
  return toEpochMillis(userData.trial_ends_at) > Date.now();
}

/**
 * L'utilisateur est-il en essai (et pas abonné payant) ?
 * Pour les bandeaux d'information — jamais pour autoriser un accès.
 *
 * @param {object|null|undefined} userData
 * @returns {boolean}
 */
export function isTrialActive(userData) {
  if (!userData) return false;
  if (userData.subscription_type === 'premium') return false;
  return toEpochMillis(userData.trial_ends_at) > Date.now();
}

/**
 * L'essai est-il TERMINÉ (par opposition à « n'a jamais eu d'essai ») ?
 *
 * Distingue les deux populations que les écrans de blocage confondent aujourd'hui :
 *   - trial_ends_at absent   → n'a jamais eu d'essai      → false
 *   - trial_ends_at dépassé  → essai consommé             → true
 *   - abonné payant          → non concerné               → false
 *
 * Sert UNIQUEMENT à choisir un message (« Votre essai est terminé » plutôt que
 * « Fonctionnalité Premium »). Ne donne aucun droit d'accès.
 *
 * @param {object|null|undefined} userData
 * @returns {boolean}
 */
export function isTrialExpired(userData) {
  if (!userData) return false;
  if (userData.subscription_type === 'premium') return false;
  const endsAt = toEpochMillis(userData.trial_ends_at);
  // endsAt === 0 = champ absent ou illisible → jamais eu d'essai, pas « terminé ».
  return endsAt > 0 && endsAt <= Date.now();
}

/**
 * Jours entiers restants avant la fin de l'essai (0 si pas d'essai en cours).
 *
 * @param {object|null|undefined} userData
 * @returns {number}
 */
export function trialDaysRemaining(userData) {
  if (!isTrialActive(userData)) return 0;
  const remainingMs = toEpochMillis(userData.trial_ends_at) - Date.now();
  return Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
}

/**
 * Libellé du décompte d'essai, prêt à afficher.
 *
 * Le dernier jour, « J-1 » est ambigu (reste-t-il un jour ou zéro ?) : on nomme
 * l'échéance explicitement. Centralisé ici pour que les 5 emplacements qui affichent
 * le décompte (badges web/mobile, sous-titres Profil, Pricing) basculent ensemble.
 *
 * @param {number} daysLeft - sortie de trialDaysRemaining()
 * @returns {string} « Dernier jour » ou « J-N »
 */
export function trialCountdownLabel(daysLeft) {
  return daysLeft <= 1 ? 'Dernier jour' : `J-${daysLeft}`;
}
